import clsx from 'clsx';
import { forwardRef, type HTMLAttributes } from 'react';

import type {
  DataTestId,
  StylingProps,
  WithChildren,
} from '@dynatrace/strato-components/core';

import { horizontalAlignmentCSS } from './Alignment.sty.js';
import { dataTableV2CellInnerCSS } from './DataTableV2Cell.sty.js';
import { dataTableV2DefaultCellCSS } from './DataTableV2DefaultCell.sty.js';
import {
  useDataTableV2,
  useDataTableV2Column,
  useDataTableV2IsHeaderContextProvider,
} from '../contexts/DataTableV2Context.js';
import { DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT } from '../features/ColumnAlignment.js';
import { SubRowsCellExpander } from '../features/SubRows/SubRowExpanders.js';

/**
 * Props for the DataTableV2.DefaultCell
 * @public
 */
export type DataTableV2DefaultCellProps = WithChildren &
  StylingProps &
  DataTestId &
  HTMLAttributes<HTMLDivElement>;

/**
 * DataTableV2 custom Cell renderer wrapper. This component automatically deals
 * with applying the correct cell styles.
 * @public
 */
export const DataTableV2DefaultCell = forwardRef<
  HTMLDivElement,
  DataTableV2DefaultCellProps
>((props, forwardedRef) => {
  const {
    children,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const table = useDataTableV2();
  const column = useDataTableV2Column();
  const isHeader = useDataTableV2IsHeaderContextProvider();

  return (
    <div
      ref={forwardedRef}
      className={clsx(
        dataTableV2DefaultCellCSS({
          rowDensity: table.getVisualVariantOptions().rowDensity,
        }),
        horizontalAlignmentCSS[
          column.columnDef.alignment ??
            DATA_TABLE_V2_DEFAULT_HORIZONTAL_ALIGNMENT
        ],
        consumerClassName,
      )}
      style={consumerStyle}
      data-testid={dataTestId}
      {...remainingProps}
    >
      {table.options.enableExpanding === true &&
        table.getSubRowIndicatorColumnId() === column.columnDef.id && (
          <SubRowsCellExpander />
        )}
      {!column.columnDef.meta?.builtin ? (
        <div
          className={clsx(
            dataTableV2CellInnerCSS({
              lineWrap: isHeader === false && column.getIsLineWrapped(),
            }),
          )}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
});

(
  DataTableV2DefaultCell as typeof DataTableV2DefaultCell & {
    displayName: string;
  }
).displayName = 'DataTableV2.DefaultCell';
